// "Mis entregas": lo que la persona tiene asignado (preparar, entregar,
// recibir), pensado para el celular y sin precios. Prepara escaneando el QR de
// cada equipo; entrega y recibe con la misma revisión de siempre.
import { el, clear } from '../../utils/dom.js';
import { Rentals } from '../../api/resources.js';
import { fmtDateTime } from '../../utils/format.js';
import { openModal } from '../../components/modal.js';
import { field, readForm } from '../../components/form.js';
import { ok, err } from '../../components/toast.js';

const TAREA = { preparar: 'Preparar', entregar: 'Entregar', recibir: 'Recibir' };
const ETAPA = { cotizacion: 'Cotización', pendiente: 'Por confirmar', confirmada: 'Confirmada', en_curso: 'Entregada', devuelta: 'Devuelta' };
const ESTADOS = [['bien', 'Bien'], ['detalle', 'Con detalle'], ['danado', 'Dañado'], ['faltante', 'Faltante']];

export default async function MisEntregasPage(outlet) {
  clear(outlet);
  let lista = [];
  try { lista = await Rentals.misEntregas(); }
  catch (ex) { outlet.innerHTML = `<div class="empty">Error: ${ex.message}</div>`; return; }
  const reload = () => MisEntregasPage(outlet);

  outlet.appendChild(el('div', { class: 'page-head' },
    el('div', {}, el('h1', {}, 'Mis entregas'), el('div', { class: 'muted' }, 'Lo que te toca preparar, entregar y recibir'))));

  if (!lista.length) {
    outlet.appendChild(el('div', { class: 'card' }, el('p', { class: 'muted' }, 'No tienes nada asignado ahora.')));
    return;
  }

  for (const r of lista) {
    const prep = r.tareas.find((t) => t.tarea === 'preparar');
    const revisados = new Set(prep?.revisados || []);
    const card = el('section', { class: 'card mb-4' },
      el('div', { class: 'flex justify-between gap-2 wrap' },
        el('strong', {}, r.code), el('span', { class: 'badge info' }, ETAPA[r.etapa] || r.etapa)),
      el('div', { class: 'text-bold mt-3' }, r.cliente),
      r.telefono ? el('a', { href: `tel:${r.telefono.replace(/[^\d+]/g, '')}`, class: 'text-sm' }, r.telefono) : null,
      r.direccion ? el('div', { class: 'text-sm muted' }, r.direccion) : null,
      el('div', { class: 'text-sm mt-3' }, el('span', { class: 'muted' }, 'Sale: '), fmtDateTime(r.inicio)),
      el('div', { class: 'text-sm' }, el('span', { class: 'muted' }, 'Vuelve: '), fmtDateTime(r.fin)),
      el('div', { class: 'flex gap-2 wrap mt-3' }, ...r.tareas.map((t) =>
        el('span', { class: `badge ${t.hecho_at ? 'success' : 'warn'}` }, `${TAREA[t.tarea]}${t.hecho_at ? ' ✓' : ''}`))),
      el('ul', { class: 'rp-lista mt-3' }, ...r.equipos.map((e) => el('li', { class: 'flex justify-between gap-2' },
        el('span', {}, `${e.nombre}${e.serie ? ` · S/N ${e.serie}` : ''} × ${e.cantidad}`),
        prep ? el('span', { class: revisados.has(e.id) ? 'rp-ok' : 'muted' }, revisados.has(e.id) ? '✓ listo' : 'falta') : null))),
    );

    const acciones = el('div', { class: 'flex gap-2 wrap mt-3' });
    if (prep && !prep.hecho_at && ['pendiente', 'confirmada'].includes(r.etapa)) {
      const input = el('input', { class: 'input', placeholder: 'Código del equipo (QR o serie)', 'aria-label': 'Código del equipo' });
      const marcar = async (codigo) => {
        try {
          const x = await Rentals.escanear(r.id, codigo);
          if (x.ok) { ok(`✓ ${x.equipo}${x.serie ? ` · ${x.serie}` : ''}`); reload(); } else err(x.motivo);
        } catch (ex) { err(ex.message); }
      };
      card.appendChild(el('div', { class: 'flex gap-2 wrap mt-3' },
        input,
        el('button', { class: 'btn', onclick: () => input.value.trim() && marcar(input.value.trim()) }, 'Marcar'),
        'BarcodeDetector' in window ? el('button', { class: 'btn', onclick: () => escanearCamara(marcar) }, 'Escanear con la cámara') : null));
      acciones.append(el('button', { class: 'btn primary', onclick: async () => {
        try { await Rentals.preparado(r.id); ok('Preparación lista'); reload(); } catch (ex) { err(ex.message); }
      } }, 'Listo, preparado'));
    }
    if (r.tareas.some((t) => t.tarea === 'entregar' && !t.hecho_at) && ['pendiente', 'confirmada'].includes(r.etapa)) {
      if (r.falta.length) card.appendChild(el('div', { class: 'rp-aviso-sm mt-3' }, `Todavía no se puede entregar: ${r.falta.join(' · ')}.`));
      acciones.append(el('button', { class: 'btn primary', disabled: r.falta.length > 0, onclick: () => revision(r, false) }, 'Entregar'));
    }
    if (r.tareas.some((t) => t.tarea === 'recibir' && !t.hecho_at) && r.etapa === 'en_curso') {
      acciones.append(el('button', { class: 'btn primary', onclick: () => revision(r, true) }, 'Recibir'));
    }
    card.appendChild(acciones);
    outlet.appendChild(card);
  }

  function revision(r, recibir) {
    const lineas = recibir ? r.equipos.filter((e) => e.cantidad > e.devueltos) : r.equipos;
    const form = el('form', { onsubmit: async (e) => {
      e.preventDefault();
      const d = readForm(form);
      const datos = lineas.map((l) => ({
        rental_item_id: l.id, estado: d[`estado_${l.id}`] || 'bien', accesorios_completos: !!d[`acc_${l.id}`],
        notas: d[`notas_${l.id}`] || undefined,
        ...(recibir ? { cantidad: Number(d[`cant_${l.id}`] || 0) } : {}),
      }));
      try {
        if (recibir) await Rentals.asisRecibir(r.id, datos.filter((x) => x.cantidad > 0), new Date().toISOString());
        else await Rentals.asisEntregar(r.id, datos);
        ok(recibir ? 'Recibido' : 'Entregado'); m.close(); reload();
      } catch (ex) { err(ex.message); }
    } }, ...lineas.map((l) => el('div', { class: 'rp-rev' },
      el('div', { class: 'text-bold' }, `${l.nombre}${l.serie ? ` · S/N ${l.serie}` : ''}`),
      recibir ? field({ name: `cant_${l.id}`, label: 'Cantidad que vuelve', type: 'number', min: 0, max: l.cantidad - l.devueltos, value: l.cantidad - l.devueltos }) : null,
      field({ name: `estado_${l.id}`, label: 'Estado', type: 'select', required: true, value: 'bien', options: ESTADOS.map(([value, label]) => ({ value, label })) }),
      field({ name: `acc_${l.id}`, label: 'Accesorios completos', type: 'checkbox', value: true }),
      field({ name: `notas_${l.id}`, label: 'Notas (opcional)' }))));
    const m = openModal({ title: `${recibir ? 'Recibir' : 'Entregar'} ${r.code}`, body: form, footer: [
      el('button', { class: 'btn', type: 'button', onclick: () => m.close() }, 'Cancelar'),
      el('button', { class: 'btn primary', onclick: () => form.requestSubmit() }, recibir ? 'Recibir' : 'Entregar'),
    ] });
  }
}

/** Lee QR con la cámara (navegadores con BarcodeDetector, como Chrome en Android). */
async function escanearCamara(alLeer) {
  let stream;
  const video = el('video', { playsinline: true, muted: true, style: 'width:100%;border-radius:12px;background:#000' });
  const m = openModal({ title: 'Apunta al código QR', body: video, onClose: () => stream && stream.getTracks().forEach((t) => t.stop()) });
  try {
    stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
    video.srcObject = stream;
    await video.play();
    // eslint-disable-next-line no-undef
    const det = new BarcodeDetector({ formats: ['qr_code', 'code_128', 'ean_13'] });
    const leer = async () => {
      if (!stream || !stream.active) return;
      try {
        const c = await det.detect(video);
        if (c.length) { m.close(); alLeer(c[0].rawValue); return; }
      } catch { /* sigue intentando */ }
      requestAnimationFrame(leer);
    };
    leer();
  } catch (ex) { m.close(); err('No se pudo abrir la cámara.'); }
}
